import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LinkifiedText, hasLinks } from "@/components/LinkifiedText";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useData } from "@/context/DataContext";
import { STATUS, formatDate, todayISO } from "@/lib/tp";
import { isHiddenUser, ADMIN_SECTION_CLIENT_ID } from "@/lib/roles";
import { Switch } from "@/components/ui/switch";
import UrgentPill from "@/components/UrgentPill";
import { toast } from "sonner";
import { CalendarDays, Trash2, Repeat, ListChecks, GitBranch, Plus, Paperclip, X, Download, Eye, History, ChevronDown } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import ChecklistEditor from "@/components/ChecklistEditor";

function toDateFromISO(s) {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function toISOFromDate(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function formatHistDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function TaskModal({ open, onOpenChange, initialClientId, editingTask }) {
  const { users, clients, addTask, updateTask, deleteTask, setTaskPanelOpen } = useData();
  // Hide super-admin (admin@taskpulse.app) from assignee/notify pickers
  const assignableUsers = users.filter(
    (u) =>
      !isHiddenUser(u) && u.role !== "cliente",
  );
  const emptySubtask = () => ({
    title: "",
    description: "",
    client_id: initialClientId || clients[0]?.id || "",
    assignee_ids: [],
    priority: 3,
    status: "da_fare",
    notify_user_id: "",
    recurring: false,
    recurring_days: 7,
    checklist: [],
    pending_subtask: null,
  });
  const [form, setForm] = useState({
    title: "",
    description: "",
    client_id: initialClientId || "",
    assignee_ids: [],
    due_date: todayISO(),
    priority: 3,
    status: "da_fare",
    notify_user_id: "",
    recurring: false,
    recurring_days: 7,
    checklist: [],
    pending_subtask: null,
    attachments: [],
  });
  const [saving, setSaving] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    await addFilesToForm(files);
  };
  const addFilesToForm = async (files) => {
    try {
      const items = await Promise.all(
        files.map(
          (f) =>
            new Promise((res, rej) => {
              if (f.size > 5 * 1024 * 1024) return rej(new Error(`${f.name}: > 5MB`));
              const r = new FileReader();
              r.onload = () =>
                res({
                  id: `att_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                  name: f.name,
                  type: f.type,
                  size: f.size,
                  data_url: r.result,
                });
              r.onerror = () => rej(r.error);
              r.readAsDataURL(f);
            }),
        ),
      );
      setForm((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...items] }));
    } catch (err) {
      toast.error(err.message || "Errore file");
    }
  };
  const [istrDragOver, setIstrDragOver] = useState(false);
  // View/Edit toggle per Istruzioni: se il campo ha contenuto vai in view (con link cliccabili),
  // il pulsante "Modifica" apre l'editor. Nuova task o campo vuoto → editor aperto.
  const [istrEdit, setIstrEdit] = useState(true);
  useEffect(() => {
    if (open) {
      const hasContent = !!(editingTask?.description || "").trim();
      setIstrEdit(!hasContent);
      setHistOpen(false);
    }
  }, [open, editingTask]);

  // Sospendi il timer di auto-refresh "Aggiorna task" mentre il pannello è aperto.
  useEffect(() => {
    if (!open) return;
    setTaskPanelOpen(true);
    return () => setTaskPanelOpen(false);
  }, [open, setTaskPanelOpen]);

  useEffect(() => {
    if (open) {
      if (editingTask) {
        setForm({
          title: editingTask.title,
          description: editingTask.description || "",
          client_id: editingTask.client_id,
          assignee_ids: editingTask.assignee_ids || [],
          due_date: editingTask.due_date || todayISO(),
          priority: editingTask.priority || 3,
          status: editingTask.status || "da_fare",
          notify_user_id: editingTask.notify_user_id || "",
          recurring: !!editingTask.recurring,
          recurring_days: editingTask.recurring_days || 7,
          checklist: Array.isArray(editingTask.checklist) ? editingTask.checklist : [],
          attachments: Array.isArray(editingTask.attachments) ? editingTask.attachments : [],
          pending_subtask: editingTask.pending_subtask
            ? {
                title: editingTask.pending_subtask.title || "",
                description: editingTask.pending_subtask.description || "",
                client_id: editingTask.pending_subtask.client_id || editingTask.client_id,
                assignee_ids: editingTask.pending_subtask.assignee_ids || [],
                priority: editingTask.pending_subtask.priority || 3,
                status: editingTask.pending_subtask.status || "da_fare",
                notify_user_id: editingTask.pending_subtask.notify_user_id || "",
                recurring: !!editingTask.pending_subtask.recurring,
                recurring_days: editingTask.pending_subtask.recurring_days || 7,
                checklist: Array.isArray(editingTask.pending_subtask.checklist)
                  ? editingTask.pending_subtask.checklist
                  : [],
                pending_subtask: editingTask.pending_subtask.pending_subtask || null,
              }
            : null,
        });
      } else {
        setForm({
          title: "",
          description: "",
          client_id: initialClientId || "",
          assignee_ids: [],
          due_date: todayISO(),
          priority: 3,
          status: "da_fare",
          notify_user_id: "",
          recurring: false,
          recurring_days: 7,
          checklist: [],
          pending_subtask: null,
          attachments: [],
        });
      }
    }
  }, [open, editingTask, initialClientId, clients]);

  const toggleSubAssignee = (id) => {
    setForm((f) => {
      if (!f.pending_subtask) return f;
      const list = f.pending_subtask.assignee_ids || [];
      return {
        ...f,
        pending_subtask: {
          ...f.pending_subtask,
          assignee_ids: list.includes(id)
            ? list.filter((x) => x !== id)
            : [...list, id],
        },
      };
    });
  };
  const patchSub = (patch) =>
    setForm((f) =>
      f.pending_subtask ? { ...f, pending_subtask: { ...f.pending_subtask, ...patch } } : f,
    );

  const toggleSub2Assignee = (id) => {
    setForm((f) => {
      const s2 = f.pending_subtask?.pending_subtask;
      if (!s2) return f;
      const list = s2.assignee_ids || [];
      return {
        ...f,
        pending_subtask: {
          ...f.pending_subtask,
          pending_subtask: {
            ...s2,
            assignee_ids: list.includes(id) ? list.filter((x) => x !== id) : [...list, id],
          },
        },
      };
    });
  };
  const patchSub2 = (patch) =>
    setForm((f) =>
      f.pending_subtask?.pending_subtask
        ? {
            ...f,
            pending_subtask: {
              ...f.pending_subtask,
              pending_subtask: { ...f.pending_subtask.pending_subtask, ...patch },
            },
          }
        : f,
    );

  const toggleAssignee = (id) => {
    setForm((f) => ({
      ...f,
      assignee_ids: f.assignee_ids.includes(id)
        ? f.assignee_ids.filter((x) => x !== id)
        : [...f.assignee_ids, id],
    }));
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Titolo richiesto");
    if (!form.client_id) return toast.error("Devi selezionare un cliente");
    if (!Array.isArray(form.assignee_ids) || form.assignee_ids.length === 0)
      return toast.error("Devi selezionare almeno un assegnatario");
    if (!form.due_date) return toast.error("Devi selezionare una data di scadenza");
    setSaving(true);
    try {
      if (editingTask) {
        await updateTask(editingTask.id, form);
        toast.success("Task aggiornata");
      } else {
        await addTask(form);
        toast.success("Task creata");
      }
      onOpenChange(false);
    } catch (e) {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editingTask) return;
    if (editingTask.trashed) {
      // Già nel cestino: eliminazione definitiva (solo admin lato backend)
      if (!confirm("Eliminare definitivamente questa task?")) return;
      await deleteTask(editingTask.id);
      toast.success("Task eliminata");
    } else {
      if (!confirm("Spostare questa task nel Cestino?")) return;
      await updateTask(editingTask.id, { trashed: true });
      toast.success("Task spostata nel cestino");
    }
    onOpenChange(false);
  };

  // NOTE: la task viene salvata SOLO al click di "Crea task" — nessun autosave.
  // La finestra si chiude solo con la X o con "Crea task" (vedi DialogContent onInteractOutside).

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="tp-glass-strong border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-white text-xl">
            {editingTask ? "Modifica task" : "Nuova task"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Titolo</Label>
            <Input
              data-testid="task-title-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bg-white/5 border-white/10 text-white h-11"
              placeholder="Es. Design nuova landing"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Assegnatari</Label>
            <div className="flex flex-wrap gap-3">
              {assignableUsers.map((u) => {
                const active = form.assignee_ids.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    data-testid={`assignee-toggle-${u.id}`}
                    className={`flex flex-col items-center gap-1.5 rounded-2xl p-2 w-[76px] border transition-all ${
                      active
                        ? "tp-brand-chip-active shadow-lg shadow-fuchsia-600/40 scale-105 bg-gradient-to-br from-fuchsia-500/40 to-pink-500/25 border-fuchsia-300/60"
                        : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"
                    }`}
                    onClick={() => toggleAssignee(u.id)}
                  >
                    <UserAvatar user={u} size={44} ring={false} />
                    <span className="text-[11px] text-center truncate max-w-full">{u.name}</span>
                  </button>
                );
              })}
              {assignableUsers.length === 0 && (
                <div className="text-xs text-zinc-500">Nessun utente. Aggiungine dal Team.</div>
              )}
            </div>
          </div>

          <div
            className={`space-y-1.5 rounded-2xl transition-all ${
              istrDragOver ? "ring-2 ring-fuchsia-400/60 bg-fuchsia-950/10 p-3" : ""
            }`}
            data-testid="task-istruzioni-dropzone"
            onDragOver={(e) => { e.preventDefault(); setIstrDragOver(true); }}
            onDragEnter={(e) => { e.preventDefault(); setIstrDragOver(true); }}
            onDragLeave={(e) => { if (e.currentTarget === e.target) setIstrDragOver(false); }}
            onDrop={async (e) => {
              e.preventDefault();
              setIstrDragOver(false);
              const files = Array.from(e.dataTransfer?.files || []);
              if (files.length) await addFilesToForm(files);
            }}
          >
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Istruzioni</Label>
              <div className="flex items-center gap-1.5">
                <input
                  ref={fileRef}
                  data-testid="task-file-input"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={onFile}
                />
                <button
                  type="button"
                  data-testid="task-attach-button"
                  onClick={() => fileRef.current?.click()}
                  title="Aggiungi file o media"
                  className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] tp-brand-gradient text-white shadow-md shadow-fuchsia-600/30"
                >
                  <Plus size={12} strokeWidth={2.6} />
                  File
                </button>
                <button
                  type="button"
                  data-testid="istruzioni-toggle-checklist"
                  onClick={() => {
                    const hasList = form.checklist && form.checklist.length > 0;
                    setForm({
                      ...form,
                      checklist: hasList
                        ? []
                        : [{ id: `c_${Date.now()}`, text: "", done: false }],
                    });
                    if (!hasList) {
                      setTimeout(() => {
                        const el = document.querySelector('[data-testid="task-checklist-input-0"]');
                        if (el) el.focus();
                      }, 60);
                    }
                  }}
                  title={form.checklist?.length > 0 ? "Rimuovi checklist" : "Aggiungi checklist"}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] border transition-colors ${
                    form.checklist?.length > 0
                      ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-300"
                      : "bg-white/5 border-white/10 text-zinc-400 hover:text-white hover:bg-white/10"
                  }`}
                >
                  <ListChecks size={12} />
                  Checklist
                </button>
              </div>
            </div>
            {istrEdit ? (
              <>
                <Textarea
                  data-testid="task-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-white/5 border-white/10 text-white min-h-[80px]"
                  placeholder="Istruzioni per svolgere la task…"
                />
                {form.description && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      data-testid="task-istruzioni-view-btn"
                      onClick={() => setIstrEdit(false)}
                      className="text-[11px] px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white border border-white/10"
                    >
                      Salva
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-1.5">
                {hasLinks(form.description) ? (
                  <LinkifiedText text={form.description} testId="task-istruzioni-preview" />
                ) : (
                  <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap break-words">
                    {form.description}
                  </div>
                )}
                <div className="flex justify-end">
                  <button
                    type="button"
                    data-testid="task-istruzioni-edit-btn"
                    onClick={() => setIstrEdit(true)}
                    className="text-[11px] px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white border border-white/10"
                  >
                    Modifica
                  </button>
                </div>
              </div>
            )}
            {form.checklist && form.checklist.length > 0 && (
              <div className="mt-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <ChecklistEditor
                  items={form.checklist}
                  onChange={(next) => setForm({ ...form, checklist: next })}
                  testIdPrefix="task-checklist"
                />
              </div>
            )}
            {form.attachments && form.attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 px-0.5">
                  {form.attachments.length} allegat{form.attachments.length === 1 ? "o" : "i"} · si eliminano al completamento
                </div>
                {form.attachments.map((a) => {
                  const isImg = (a.type || "").startsWith("image/");
                  return (
                    <div
                      key={a.id}
                      data-testid={`task-attachment-${a.id}`}
                      className="flex items-center gap-2 rounded-lg bg-white/5 border border-white/10 p-2"
                    >
                      <a
                        href={a.data_url}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0"
                        title="Anteprima"
                      >
                        {isImg ? (
                          <img
                            src={a.data_url}
                            alt={a.name}
                            className="h-10 w-10 rounded object-cover hover:opacity-80 transition-opacity"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded bg-white/10 flex items-center justify-center hover:bg-white/15 transition-colors">
                            <Paperclip size={16} className="text-zinc-400" />
                          </div>
                        )}
                      </a>
                      <a
                        href={a.data_url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 min-w-0 text-xs text-zinc-200 truncate hover:text-white"
                        title="Apri"
                      >
                        {a.name}
                      </a>
                      <a
                        href={a.data_url}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-white/10"
                        title="Anteprima"
                      >
                        <Eye size={14} />
                      </a>
                      <a
                        href={a.data_url}
                        download={a.name}
                        className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-white/10"
                        title="Scarica"
                      >
                        <Download size={14} />
                      </a>
                      <button
                        type="button"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            attachments: (prev.attachments || []).filter((x) => x.id !== a.id),
                          }))
                        }
                        className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                        title="Rimuovi"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Cliente</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="task-client-select" className="bg-white/5 border-white/10 text-white h-11">
                  <SelectValue placeholder="Seleziona cliente" />
                </SelectTrigger>
                <SelectContent className="tp-glass-strong border-white/10 text-white">
                  {clients
                    .filter(
                      (c) => c.id !== ADMIN_SECTION_CLIENT_ID || c.id === form.client_id,
                    )
                    .map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Scadenza <span className="text-fuchsia-400">*</span></Label>
              <Popover modal={true} open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    data-testid="task-date-button"
                    variant="outline"
                    className="w-full h-11 justify-start bg-white/5 border-white/10 text-white hover:bg-white/10"
                  >
                    <CalendarDays size={16} className="mr-2 text-zinc-400" />
                    {form.due_date ? formatDate(form.due_date) : "Seleziona data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  sideOffset={8}
                  className="tp-glass-strong border-white/10 p-0 text-white w-auto z-[70]"
                >
                  <Calendar
                    mode="single"
                    weekStartsOn={1}
                    disabled={(d) => { const w = d.getDay(); return w === 0 || w === 6; }}
                    selected={toDateFromISO(form.due_date)}
                    onSelect={(d) => {
                      setForm({ ...form, due_date: toISOFromDate(d) });
                      setDateOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Urgente</Label>
              <div className="h-11 flex items-center px-3 rounded-md bg-white/5 border border-white/10">
                <UrgentPill
                  urgent={form.priority === 5}
                  onChange={(v) => setForm({ ...form, priority: v ? 5 : 3 })}
                  testId="task-urgent-toggle"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Stato</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="task-status-select" className="bg-white/5 border-white/10 text-white h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="tp-glass-strong border-white/10 text-white">
                  {Object.values(STATUS).map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: s.text }} />
                        {s.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">
              Notifica al completamento
            </Label>
            <Select
              value={form.notify_user_id || "none"}
              onValueChange={(v) => setForm({ ...form, notify_user_id: v === "none" ? "" : v })}
            >
              <SelectTrigger data-testid="task-notify-select" className="bg-white/5 border-white/10 text-white h-11">
                <SelectValue placeholder="Nessuno" />
              </SelectTrigger>
              <SelectContent className="tp-glass-strong border-white/10 text-white">
                <SelectItem value="none">Nessuno</SelectItem>
                {assignableUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    Notifica <span className="ml-1 font-medium">{u.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-zinc-500">
              Quando la task passa a <span className="text-zinc-300">Completato</span>, l&apos;utente selezionato riceve una notifica.
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-start gap-4">
            <div className="h-10 w-10 rounded-xl tp-brand-gradient flex items-center justify-center shrink-0 shadow-md shadow-fuchsia-600/30">
              <Repeat size={16} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium text-white">Task ricorrente</Label>
                <Switch
                  checked={form.recurring}
                  onCheckedChange={(v) => setForm({ ...form, recurring: v })}
                  data-testid="task-recurring-switch"
                />
              </div>
              {form.recurring && (
                <div className="mt-2 flex items-center gap-2">
                  <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Ogni</Label>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    data-testid="task-recurring-days-input"
                    value={form.recurring_days}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        recurring_days: Math.max(1, Math.min(365, parseInt(e.target.value) || 1)),
                      })
                    }
                    className="bg-white/5 border-white/10 text-white h-8 w-20 text-sm"
                  />
                  <span className="text-xs text-zinc-400">giorni</span>
                </div>
              )}
              <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
                Al completamento verrà creata automaticamente una copia con stesso cliente,
                assegnatari e priorità, in scadenza{" "}
                <span className="text-zinc-300">
                  {form.recurring ? form.recurring_days : 7} giorni dopo
                </span>
                . Si ripete a ogni completamento finché non elimini la task.
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0 shadow-md shadow-emerald-600/30">
                <GitBranch size={16} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <Label className="text-sm font-medium text-white">Sotto-task</Label>
                  <Switch
                    checked={!!form.pending_subtask}
                    onCheckedChange={(v) =>
                      setForm({ ...form, pending_subtask: v ? emptySubtask() : null })
                    }
                    data-testid="task-subtask-switch"
                  />
                </div>
                <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
                  Definisci qui la task che verrà creata automaticamente al completamento di
                  questa. La scadenza sarà{" "}
                  <span className="text-zinc-300">la data di completamento</span>.
                </div>
              </div>
            </div>

            {form.pending_subtask && (
              <div
                className="mt-4 pt-4 border-t border-white/10 space-y-4"
                data-testid="subtask-panel"
              >
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">
                    Titolo sotto-task
                  </Label>
                  <Input
                    data-testid="subtask-title-input"
                    value={form.pending_subtask.title}
                    onChange={(e) => patchSub({ title: e.target.value })}
                    className="bg-white/5 border-white/10 text-white h-11"
                    placeholder="Es. Invio report al cliente"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">
                    Assegnatari
                  </Label>
                  <div className="flex flex-wrap gap-3">
                    {assignableUsers.map((u) => {
                      const active = (form.pending_subtask.assignee_ids || []).includes(u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          data-testid={`subtask-assignee-toggle-${u.id}`}
                          onClick={() => toggleSubAssignee(u.id)}
                          className={`flex flex-col items-center gap-1.5 rounded-2xl p-2 w-[76px] border transition-all ${
                            active
                              ? "tp-brand-chip-active shadow-lg shadow-fuchsia-600/20 scale-105"
                              : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"
                          }`}
                        >
                          <UserAvatar user={u} size={44} ring={false} />
                          <span className="text-[11px] text-center truncate max-w-full">
                            {u.name}
                          </span>
                        </button>
                      );
                    })}
                    {assignableUsers.length === 0 && (
                      <div className="text-xs text-zinc-500">
                        Nessun utente. Aggiungine dal Team.
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">
                      Istruzioni
                    </Label>
                    <button
                      type="button"
                      data-testid="subtask-toggle-checklist"
                      onClick={() => {
                        const hasList =
                          form.pending_subtask.checklist &&
                          form.pending_subtask.checklist.length > 0;
                        patchSub({
                          checklist: hasList
                            ? []
                            : [{ id: `sc_${Date.now()}`, text: "", done: false }],
                        });
                      }}
                      title={
                        form.pending_subtask.checklist?.length > 0
                          ? "Rimuovi checklist"
                          : "Aggiungi checklist"
                      }
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] border transition-colors ${
                        form.pending_subtask.checklist?.length > 0
                          ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-300"
                          : "bg-white/5 border-white/10 text-zinc-400 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      <ListChecks size={12} />
                      Checklist
                    </button>
                  </div>
                  <Textarea
                    data-testid="subtask-description-input"
                    value={form.pending_subtask.description}
                    onChange={(e) => patchSub({ description: e.target.value })}
                    className="bg-white/5 border-white/10 text-white min-h-[80px]"
                    placeholder="Istruzioni per svolgere la sotto-task…"
                  />
                  {form.pending_subtask.checklist &&
                    form.pending_subtask.checklist.length > 0 && (
                      <div className="mt-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                        <ChecklistEditor
                          items={form.pending_subtask.checklist}
                          onChange={(next) => patchSub({ checklist: next })}
                          testIdPrefix="subtask-checklist"
                        />
                      </div>
                    )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">
                      Cliente
                    </Label>
                    <Select
                      value={form.pending_subtask.client_id}
                      onValueChange={(v) => patchSub({ client_id: v })}
                    >
                      <SelectTrigger
                        data-testid="subtask-client-select"
                        className="bg-white/5 border-white/10 text-white h-11"
                      >
                        <SelectValue placeholder="Seleziona cliente" />
                      </SelectTrigger>
                      <SelectContent className="tp-glass-strong border-white/10 text-white">
                        {clients
                          .filter(
                            (c) =>
                              c.id !== ADMIN_SECTION_CLIENT_ID ||
                              c.id === form.pending_subtask.client_id,
                          )
                          .map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              <span className="inline-flex items-center gap-2">
                                <span
                                  className="h-2 w-2 rounded-full"
                                  style={{ background: c.color }}
                                />
                                {c.name}
                              </span>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">
                      Urgente
                    </Label>
                    <div className="h-11 flex items-center px-3 rounded-md bg-white/5 border border-white/10">
                      <UrgentPill
                        urgent={form.pending_subtask.priority === 5}
                        onChange={(v) => patchSub({ priority: v ? 5 : 3 })}
                        testId="subtask-urgent-toggle"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">
                      Stato iniziale
                    </Label>
                    <Select
                      value={form.pending_subtask.status}
                      onValueChange={(v) => patchSub({ status: v })}
                    >
                      <SelectTrigger
                        data-testid="subtask-status-select"
                        className="bg-white/5 border-white/10 text-white h-11"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="tp-glass-strong border-white/10 text-white">
                        {Object.values(STATUS).map((s) => (
                          <SelectItem key={s.key} value={s.key}>
                            <span className="inline-flex items-center gap-2">
                              <span
                                className="h-2 w-2 rounded-full"
                                style={{ background: s.text }}
                              />
                              {s.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">
                      Notifica al completamento
                    </Label>
                    <Select
                      value={form.pending_subtask.notify_user_id || "none"}
                      onValueChange={(v) =>
                        patchSub({ notify_user_id: v === "none" ? "" : v })
                      }
                    >
                      <SelectTrigger
                        data-testid="subtask-notify-select"
                        className="bg-white/5 border-white/10 text-white h-11"
                      >
                        <SelectValue placeholder="Nessuno" />
                      </SelectTrigger>
                      <SelectContent className="tp-glass-strong border-white/10 text-white">
                        <SelectItem value="none">Nessuno</SelectItem>
                        {assignableUsers.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            Notifica <span className="ml-1 font-medium">{u.name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 flex items-start gap-3">
                  <div className="h-9 w-9 rounded-xl tp-brand-gradient flex items-center justify-center shrink-0">
                    <Repeat size={14} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <Label className="text-sm font-medium text-white">Ricorrente</Label>
                      <Switch
                        checked={form.pending_subtask.recurring}
                        onCheckedChange={(v) => patchSub({ recurring: v })}
                        data-testid="subtask-recurring-switch"
                      />
                    </div>
                    {form.pending_subtask.recurring && (
                      <div className="mt-2 flex items-center gap-2">
                        <Label className="text-[11px] uppercase tracking-wider text-zinc-400">
                          Ogni
                        </Label>
                        <Input
                          type="number"
                          min={1}
                          max={365}
                          data-testid="subtask-recurring-days-input"
                          value={form.pending_subtask.recurring_days}
                          onChange={(e) =>
                            patchSub({
                              recurring_days: Math.max(
                                1,
                                Math.min(365, parseInt(e.target.value) || 1),
                              ),
                            })
                          }
                          className="bg-white/5 border-white/10 text-white h-8 w-20 text-sm"
                        />
                        <span className="text-xs text-zinc-400">giorni</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Nested sub-task inside sub-task */}
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
                      <GitBranch size={14} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <Label className="text-sm font-medium text-white">Ulteriore sotto-task</Label>
                        <Switch
                          checked={!!form.pending_subtask.pending_subtask}
                          onCheckedChange={(v) =>
                            patchSub({ pending_subtask: v ? emptySubtask() : null })
                          }
                          data-testid="subtask2-switch"
                        />
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
                        Verrà creata al completamento della sotto-task, con scadenza pari alla data di completamento.
                      </div>
                    </div>
                  </div>

                  {form.pending_subtask.pending_subtask && (
                    <div
                      className="mt-3 pt-3 border-t border-white/10 space-y-3"
                      data-testid="subtask2-panel"
                    >
                      <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-400">
                          Titolo
                        </Label>
                        <Input
                          data-testid="subtask2-title-input"
                          value={form.pending_subtask.pending_subtask.title}
                          onChange={(e) => patchSub2({ title: e.target.value })}
                          className="bg-white/5 border-white/10 text-white h-10"
                          placeholder="Es. Follow-up"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-400">
                          Istruzioni
                        </Label>
                        <Textarea
                          data-testid="subtask2-description-input"
                          value={form.pending_subtask.pending_subtask.description}
                          onChange={(e) => patchSub2({ description: e.target.value })}
                          className="bg-white/5 border-white/10 text-white min-h-[60px] text-sm"
                          placeholder="Istruzioni…"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-400">
                          Assegnatari
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {assignableUsers.map((u) => {
                            const active = (
                              form.pending_subtask.pending_subtask.assignee_ids || []
                            ).includes(u.id);
                            return (
                              <button
                                key={u.id}
                                type="button"
                                data-testid={`subtask2-assignee-toggle-${u.id}`}
                                onClick={() => toggleSub2Assignee(u.id)}
                                className={`flex flex-col items-center gap-1 rounded-xl p-1.5 w-[62px] border transition-all ${
                                  active
                                    ? "tp-brand-chip-active shadow-md shadow-fuchsia-600/40 scale-105 bg-gradient-to-br from-fuchsia-500/40 to-pink-500/25 border-fuchsia-300/60"
                                    : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"
                                }`}
                              >
                                <UserAvatar user={u} size={32} ring={false} />
                                <span className="text-[10px] text-center truncate max-w-full">
                                  {u.name}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">
                            Cliente
                          </Label>
                          <Select
                            value={form.pending_subtask.pending_subtask.client_id}
                            onValueChange={(v) => patchSub2({ client_id: v })}
                          >
                            <SelectTrigger
                              data-testid="subtask2-client-select"
                              className="bg-white/5 border-white/10 text-white h-10"
                            >
                              <SelectValue placeholder="Cliente" />
                            </SelectTrigger>
                            <SelectContent className="tp-glass-strong border-white/10 text-white">
                              {clients
                                .filter(
                                  (c) =>
                                    c.id !== ADMIN_SECTION_CLIENT_ID ||
                                    c.id === form.pending_subtask.pending_subtask.client_id,
                                )
                                .map((c) => (
                                  <SelectItem key={c.id} value={c.id}>
                                    <span className="inline-flex items-center gap-2">
                                      <span
                                        className="h-2 w-2 rounded-full"
                                        style={{ background: c.color }}
                                      />
                                      {c.name}
                                    </span>
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">
                            Urgente
                          </Label>
                          <div className="h-10 flex items-center px-3 rounded-md bg-white/5 border border-white/10">
                            <UrgentPill
                              urgent={form.pending_subtask.pending_subtask.priority === 5}
                              onChange={(v) => patchSub2({ priority: v ? 5 : 3 })}
                              testId="subtask2-urgent-toggle"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {editingTask && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
            <button
              type="button"
              data-testid="task-history-toggle"
              onClick={() => setHistOpen((v) => !v)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors"
            >
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-zinc-500 to-zinc-700 flex items-center justify-center shrink-0 shadow-md shadow-black/30">
                <History size={16} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white">Cronologia</div>
                <div className="text-[11px] text-zinc-500">
                  {Array.isArray(editingTask.history) && editingTask.history.length > 0
                    ? `${editingTask.history.length} event${editingTask.history.length === 1 ? "o" : "i"}`
                    : "Nessun evento registrato"}
                </div>
              </div>
              <ChevronDown
                size={18}
                className={`text-zinc-400 transition-transform ${histOpen ? "rotate-180" : ""}`}
              />
            </button>
            {histOpen && (
              <div
                data-testid="task-history-panel"
                className="px-4 pb-4 pt-2 space-y-3 max-h-72 overflow-y-auto border-t border-white/10"
              >
                {!Array.isArray(editingTask.history) || editingTask.history.length === 0 ? (
                  <div className="text-[11px] text-zinc-500 py-1">
                    Nessuna cronologia disponibile per questa task. Le modifiche effettuate da ora in poi verranno registrate qui.
                  </div>
                ) : (
                  editingTask.history
                  .slice()
                  .reverse()
                  .map((ev) => (
                    <div
                      key={ev.id}
                      data-testid={`task-history-event-${ev.id}`}
                      className="relative pl-4 border-l-2 border-white/10"
                    >
                      <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-fuchsia-400" />
                      <div className="text-xs text-white">
                        <span className="font-medium">{ev.actor_name || "Utente"}</span>{" "}
                        <span className="text-zinc-400">
                          {ev.type === "created" ? "ha creato la task" : "ha modificato la task"}
                        </span>
                      </div>
                      <div className="text-[11px] text-zinc-500">{formatHistDate(ev.at)}</div>
                      {Array.isArray(ev.changes) && ev.changes.length > 0 && (
                        <ul className="mt-1.5 space-y-1">
                          {ev.changes.map((c, i) => (
                            <li key={i} className="text-[11px] text-zinc-300 leading-relaxed">
                              {c.field === "checklist" ? (
                                <>
                                  <span className="text-zinc-400">{c.label}:</span>
                                  <ul className="ml-3 mt-0.5 list-disc list-inside space-y-0.5 text-zinc-400">
                                    {(c.details || []).map((d, j) => (
                                      <li key={j} className="break-words">{d}</li>
                                    ))}
                                  </ul>
                                </>
                              ) : (
                                <span className="break-words">
                                  <span className="text-zinc-400">{c.label}:</span>{" "}
                                  <span className="line-through text-zinc-500">{c.from || "—"}</span>{" "}
                                  <span className="text-zinc-500">→</span>{" "}
                                  <span className="text-zinc-100">{c.to || "—"}</span>
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          {editingTask ? (
            <Button
              variant="ghost"
              onClick={remove}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid="task-delete-button"
            >
              <Trash2 size={14} className="mr-1.5" /> Elimina
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-zinc-400 hover:text-white hover:bg-white/10"
            >
              Annulla
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              data-testid="task-save-button"
              className="tp-brand-btn rounded-xl"
            >
              {saving ? "Salvo..." : editingTask ? "Salva modifiche" : "Crea task"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
